// Designer wrapper around the shared "My Approvals" inbox
// (@twasta/page-runtime/txn/approvals). The shared component is transport-
// free; this shim binds it to the platform's /api/livepreview/* endpoints
// via makeDesignerScreenRuntime — plus the designer's transaction-model
// loader (loadTransactionObject) as `getModel` — so every existing call
// site (RunTopBar, LivePreviewTab) keeps working unchanged.

import { useMemo } from 'react'
import {
  ApprovalsInbox as CoreApprovalsInbox,
  type ApprovalsInboxRuntime,
} from '@twasta/page-runtime/txn/approvals'
import { loadModel } from './loadModel'
import { makeDmsClient } from '@/api/dms'
import { makeDesignerScreenRuntime } from './designerScreenRuntime'

interface Props {
  project: string
  onClose: () => void
  /** Lets the host re-read the pending count after a decision. */
  onCountChange?: (count: number) => void
  /** Open on everyone's pending queue (admin only — server-gated). */
  initialShowAll?: boolean
  /** Land on this instance's review panel (the row the user already picked). */
  initialInstanceId?: string
  /** Render inside the host's surface (a worklist tab) instead of as a modal. */
  embedded?: boolean
}



export default function ApprovalsInbox({
  project, onClose, onCountChange, initialShowAll, initialInstanceId, embedded,
}: Props) {
  // makeDesignerScreenRuntime already binds the FULL approvals family (its
  // `approvals` is the tight ApprovalsInboxApprovals shape); adding getModel
  // here completes the ApprovalsInboxRuntime contract with no cast.
  //
  // `dms` is what lets the inbox review DOCUMENT approvals (obj_name
  // `DMS_<repo>`) as documents: they ride the same approval engine and land in
  // the same list, but there is no transaction model behind them and the
  // decision has to go to the repository, which is what this client reaches.
  const runtime = useMemo<ApprovalsInboxRuntime>(() => ({
    ...makeDesignerScreenRuntime(project),
    getModel: (objName: string) => loadModel(project, objName),
    dms: (repo: string) => makeDmsClient(project, repo),
  }), [project])

  return (
    <CoreApprovalsInbox
      runtime={runtime}
      project={project}
      onClose={onClose}
      onCountChange={onCountChange}
      initialShowAll={initialShowAll}
      initialInstanceId={initialInstanceId}
      embedded={embedded}
    />
  )
}
